import { forwardRef } from 'preact/compat';
import { useImperativeHandle, useMemo } from 'preact/hooks';

import { html } from '@shared/utils/rendering/html';

import Button from '@shared/components/buttons';
import Table from '@shared/components/tables';
import { DropdownNodePicker, VisualPickerModes, mergeNodes } from '@shared/components/node-picker';

import api from '@core/utils/api/api';
import {
    UserHierarchyNodeColumns,
    UserHierarchyNodeFilters,
} from '@core/configs/admin/users/user-hierarchy-node-table.config';

// Any active node is assignable, not just leaves: consuming systems (e.g. EWIS) curate their own
// much smaller trees from this same NodeId space, and the nodes they treat as assignable are
// frequently non-leaf here since Portal's tree runs far deeper (down to individual cost-centre
// codes). Restricting to IsLeaf made those nodes unselectable.
//
// Portal's real hierarchy is ~37,000 rows, so the picker runs on Venus's lazy data-source path:
// the tree loads a layer at a time and the dropdown searches server-side, rather than pulling the
// whole hierarchy into the browser.
const CHILDREN_PAGE_SIZE = 500; // HierarchyNodesController.MaxChildrenTake

// The API returns hierarchyNodeId/name/fullPath (the entity's own column names); the picker reads
// nodeId/description/path. fullPath is "|"-delimited, which Venus's path renderer already splits on.
const toPickerNode = (node) => ({
    nodeId: node.hierarchyNodeId,
    parentNodeId: node.parentNodeId ?? null,
    description: node.name,
    path: node.fullPath,
    isLeaf: node.isLeaf,
});

async function loadChildren(nodeId, signal) {
    const nodes = [];
    let totalCount = Infinity;

    while (nodes.length < totalCount) {
        const { data } = await api.get(`/hierarchy-nodes/${nodeId}/children`, {
            params: { take: CHILDREN_PAGE_SIZE, skip: nodes.length },
            signal,
        });

        const items = data?.items ?? [];
        totalCount = data?.totalCount ?? 0;
        nodes.push(...items);

        if (items.length === 0) break;
    }

    return nodes.map(toPickerNode);
}

// Module-level so its identity is stable — Venus restarts loading whenever the data source changes.
const hierarchyDataSource = {
    loadRoots: async (signal) => {
        const { data } = await api.get('/hierarchy-nodes/roots', { signal });
        return (data ?? []).map(toPickerNode);
    },

    loadChildren,

    // Spine from the root down to the node, plus each spine node's children, so the tree opens at
    // a searched-for node with its siblings visible rather than as a bare line.
    loadPath: async (nodeId, signal) => {
        const { data } = await api.get(`/hierarchy-nodes/${nodeId}/path`, { signal });
        const spine = (data ?? []).map(toPickerNode);

        if (spine.length === 0) return spine;

        const childSets = await Promise.all(
            spine.slice(0, -1).map((node) => loadChildren(node.nodeId, signal))
        );

        return childSets.reduce((merged, children) => mergeNodes(merged, children), spine);
    },

    searchNodes: async (term, signal) => {
        const { data } = await api.get('/hierarchy-nodes/search', {
            params: { q: term },
            signal,
        });
        return (data ?? []).map(toPickerNode);
    },
};

const PICKER_CONFIG = {
    visualPicker: VisualPickerModes.Modal,
    searchPlaceholder: 'Search organisational nodes…',
    subject: 'organisational nodes',
    modalTitle: 'Select Organisational Node',
};

const resolveEntityId = (entity) => {
    if (entity && typeof entity === 'object') {
        return entity.value ?? entity.id ?? entity.nodeId ?? null;
    }

    return entity ?? null;
};

const UserHierarchyNodes = forwardRef(({ inputProps, layoutSettings }, ref) => {
    // The picker never holds a committed value (see value=null below), so there is nothing to
    // clear — kept so the form's fieldRef contract still resolves.
    useImperativeHandle(ref, () => ({
        clear: () => {},
    }));

    const addNode = ({ nodeId, path, node }) => {
        // Full path, matching what the server returns for saved rows: many nodes share a name, so
        // only the path identifies which one is linked. node.path is the API's own fullPath;
        // `path` (the picker's formatted string) is the fallback.
        const value = {
            value: nodeId,
            text: node?.path || path || node?.description || String(nodeId),
        };

        const fieldValue = inputProps.field.value ?? [];
        const values = (Array.isArray(fieldValue) ? fieldValue : [fieldValue]).filter(Boolean);

        const hasNode = values.some((entity) => +resolveEntityId(entity) === +value.value);
        const newValues = hasNode ? values : [...values, value];

        inputProps.field?.setValue(newValues, inputProps.field);
    };

    // Receives id via Button eventProps — avoids an inline arrow function per re-render
    const unlinkNode = (event, id) => {
        const fieldValue = inputProps.field.value ?? [];
        const prev = (Array.isArray(fieldValue) ? fieldValue : [fieldValue]).filter(Boolean);
        const next = prev.filter((x) => +resolveEntityId(x) !== +id);

        inputProps.field?.setValue(next, inputProps.field);
    };

    const rowActions = (row) => {
        const rowId = row.id ?? row.value;
        return html`
            <div class="row-actions">
                <${Button}
                    buttonType="tertiary"
                    label=${{ text: 'Unlink' }}
                    onClick=${unlinkNode}
                    eventProps=${{ id: rowId }}
                />
            </div>
        `;
    };

    const tableData = useMemo(() => {
        const rows = (
            Array.isArray(inputProps?.field?.value)
                ? inputProps.field.value
                : [inputProps?.field?.value]
        ).filter(Boolean);

        return {
            total: rows.length,
            rows,
            isLoading: false,
        };
    }, [inputProps?.field?.value]);

    // value stays null: this field adds to the linked-nodes table below rather than holding a
    // single selection, so the picker resets to empty after every choice.
    return html`
        <div class="row">
            <div class="col-4">
                <${DropdownNodePicker}
                    id="user-hierarchy-node-picker"
                    label="Organisational Node"
                    value=${null}
                    dataSource=${hierarchyDataSource}
                    disabled=${Boolean(layoutSettings?.disabled)}
                    config=${PICKER_CONFIG}
                    onSelect=${addNode}
                />
            </div>
        </div>

        <${Table}
            config=${{
                columns: UserHierarchyNodeColumns,
                filters: UserHierarchyNodeFilters,
                actions: rowActions,
            }}
            data=${tableData}
        />
    `;
});

export default UserHierarchyNodes;
